import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import FramedAvatar from '../components/FramedAvatar';
import {
  ArrowLeft, MapPin, Trophy, MessageSquare, Award, Home,
  Building2, TrendingUp, Calendar, User as UserIcon
} from 'lucide-react';
import { toast } from 'sonner';
import errMsg from '../utils/apiError';

const INV_TYPE_LABELS = {
  appartement: 'Appartement',
  maison: 'Maison',
  immeuble: 'Immeuble',
  lmnp: 'LMNP',
  lmp: 'LMP',
  parking: 'Parking',
  local_commercial: 'Local commercial',
  terrain: 'Terrain',
  sci: 'SCI',
  autre: 'Autre',
};
const INV_STATUS_LABELS = {
  recherche: { label: 'En recherche', color: 'bg-blue-500/15 text-blue-700 border-blue-300' },
  signe: { label: 'Signé', color: 'bg-purple-500/15 text-purple-700 border-purple-300' },
  loue: { label: 'Loué', color: 'bg-green-500/15 text-green-700 border-green-300' },
  revendu: { label: 'Revendu', color: 'bg-amber-500/15 text-amber-700 border-amber-300' },
};

export default function MemberProfilePage() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { api, user: me } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) return;
    (async () => {
      try {
        const r = await api.get(`/members/${userId}/profile`);
        setProfile(r.data);
      } catch (e) {
        toast.error(errMsg(e, 'Impossible de charger le profil'));
      } finally {
        setLoading(false);
      }
    })();
  }, [api, userId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <UserIcon className="w-10 h-10 text-primary animate-pulse" />
      </div>
    );
  }
  if (!profile) return null;

  const { user, stats, badges, victories, stories, recent_messages, investments } = profile;
  const isMe = me?.id === user.id;

  return (
    <div className="min-h-screen bg-background" data-testid="member-profile-page">
      <div className="max-w-4xl mx-auto p-4 sm:p-6">
        <div className="flex items-center gap-3 mb-6">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-2xl font-heading">Profil membre</h1>
        </div>

        {/* Header card */}
        <Card className="mb-6 overflow-hidden">
          <div className="h-24 bg-gradient-to-br from-primary/20 via-amber-400/10 to-primary/5" />
          <CardContent className="pt-0 relative">
            <div className="flex flex-col sm:flex-row items-start sm:items-end gap-4 -mt-12">
              <FramedAvatar
                src={user.avatar_url}
                name={`${user.first_name} ${user.last_name}`}
                size="xl"
                role={user.role}
                seniority={user.seniority}
                profileFrame={user.profile_frame}
              />
              <div className="flex-1 pt-2 sm:pt-0 pb-2">
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="text-xl font-heading">{user.first_name} {user.last_name}</h2>
                  {user.role && <Badge variant="outline">{user.role}</Badge>}
                  {user.sub_role && <Badge variant="secondary">{user.sub_role}</Badge>}
                </div>
                {user.city && (
                  <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                    <MapPin className="w-3 h-3" />
                    {user.city}
                  </p>
                )}
                {user.bio && (
                  <p className="text-sm mt-2 whitespace-pre-line">{user.bio}</p>
                )}
              </div>
              <div className="flex gap-2">
                {!isMe && (
                  <Button variant="outline" size="sm" onClick={() => navigate(`/dm/${user.id}`)}>
                    <MessageSquare className="w-4 h-4 mr-1" />
                    Message
                  </Button>
                )}
                {isMe && (
                  <Button variant="outline" size="sm" onClick={() => navigate('/profile')}>
                    Éditer mon profil
                  </Button>
                )}
              </div>
            </div>

            {/* Stats grid */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6">
              <StatCell icon={<TrendingUp className="w-4 h-4" />} label="Niveau" value={user.level ?? '—'} />
              <StatCell icon={<Trophy className="w-4 h-4" />} label="Victoires" value={stats.victories_count} />
              <StatCell icon={<Award className="w-4 h-4" />} label="Badges" value={stats.badges_count} />
              <StatCell icon={<Building2 className="w-4 h-4" />} label="Investissements" value={stats.investments_count} />
            </div>
          </CardContent>
        </Card>

        <Tabs defaultValue="investments">
          <TabsList>
            <TabsTrigger value="investments" data-testid="tab-investments">
              <Building2 className="w-4 h-4 mr-1" /> Investissements ({(investments || []).length})
            </TabsTrigger>
            <TabsTrigger value="victories" data-testid="tab-victories">
              <Trophy className="w-4 h-4 mr-1" /> Victoires ({(victories || []).length})
            </TabsTrigger>
            <TabsTrigger value="stories" data-testid="tab-stories">
              Stories ({(stories || []).length})
            </TabsTrigger>
            <TabsTrigger value="messages" data-testid="tab-messages">
              Derniers messages ({(recent_messages || []).length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="investments" className="mt-4 space-y-3">
            {(investments || []).length === 0 ? (
              <EmptyState icon={<Building2 />} title="Aucun investissement partagé" desc="Ce membre n'a pas encore ajouté d'investissement, ou choisit de les garder privés." />
            ) : (
              (investments || []).map((inv) => (
                <Card key={inv.id}>
                  <CardContent className="p-4 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                      <Home className="w-5 h-5" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{INV_TYPE_LABELS[inv.type] || inv.type}</span>
                        <span className="text-muted-foreground">·</span>
                        <span className="text-sm text-muted-foreground flex items-center gap-1">
                          <MapPin className="w-3 h-3" /> {inv.city}
                        </span>
                      </div>
                      {inv.notes && <p className="text-xs text-muted-foreground mt-1 line-clamp-1">{inv.notes}</p>}
                    </div>
                    <Badge className={`${INV_STATUS_LABELS[inv.status]?.color || ''} border`} variant="outline">
                      {INV_STATUS_LABELS[inv.status]?.label || inv.status}
                    </Badge>
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>

          <TabsContent value="victories" className="mt-4 space-y-3">
            {(victories || []).length === 0 ? (
              <EmptyState icon={<Trophy />} title="Aucune victoire" desc="Ce membre n'a pas encore partagé de victoire." />
            ) : (
              victories.map((v) => (
                <Card key={v.id}>
                  <CardContent className="p-4">
                    <p className="font-medium">{v.title}</p>
                    {v.description && <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{v.description}</p>}
                    <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {new Date(v.created_at).toLocaleDateString('fr-FR')}
                    </p>
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>

          <TabsContent value="stories" className="mt-4">
            {(stories || []).length === 0 ? (
              <EmptyState icon={<UserIcon />} title="Aucune story active" />
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {stories.map((s) => (
                  <Card key={s.id} className="overflow-hidden">
                    {s.media_url && (
                      <div className="aspect-square bg-muted overflow-hidden">
                        <img src={s.media_url} alt="" className="w-full h-full object-cover" />
                      </div>
                    )}
                    <CardContent className="p-3">
                      <p className="text-xs text-muted-foreground">
                        {new Date(s.created_at).toLocaleDateString('fr-FR')}
                      </p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="messages" className="mt-4 space-y-2">
            {(recent_messages || []).length === 0 ? (
              <EmptyState icon={<MessageSquare />} title="Aucun message public récent" />
            ) : (
              recent_messages.map((m) => (
                <Card key={m.id}>
                  <CardContent className="p-3">
                    <p className="text-sm whitespace-pre-line line-clamp-3">{m.content}</p>
                    <p className="text-xs text-muted-foreground mt-2">
                      {new Date(m.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

const StatCell = ({ icon, label, value }) => (
  <div className="bg-secondary/30 border border-border rounded-lg p-3 text-center">
    <div className="flex items-center justify-center gap-1 text-muted-foreground text-xs">
      {icon} <span>{label}</span>
    </div>
    <p className="text-xl font-bold mt-1">{value}</p>
  </div>
);

const EmptyState = ({ icon, title, desc }) => (
  <Card>
    <CardContent className="p-8 text-center text-muted-foreground">
      <div className="w-12 h-12 mx-auto mb-2 opacity-40">{React.cloneElement(icon, { className: 'w-full h-full' })}</div>
      <p className="font-medium">{title}</p>
      {desc && <p className="text-sm mt-1 max-w-md mx-auto">{desc}</p>}
    </CardContent>
  </Card>
);
